/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import {
  FoodmaxapipbHierarchyEntity,
  FoodmaxapipbItemV2,
} from "generated/foodmaxapi.type"
import { Department, SubDepartment, Item } from "types/models/menu"

// Type alias for convenience
export type HierarchyEntity = FoodmaxapipbHierarchyEntity

/**
 * Maps HierarchyEntity from MenuV3Hierarchies API to Department type
 */
export const mapHierarchyEntityToDepartment = (
  entity: HierarchyEntity,
): Department => {
  return {
    ...entity,
    itemClassID: entity.id,
    itemClassName: entity.name,
    totalUnavailableItems: entity.totalOutOfStocksItems,
    subDepartments: [], // Will be populated separately if needed
  }
}

/**
 * Maps HierarchyEntity from MenuV3Hierarchies API to SubDepartment type
 */
export const mapHierarchyEntityToSubDepartment = (
  entity: HierarchyEntity,
  departmentID?: string,
  departmentName?: string,
): SubDepartment => {
  return {
    ...entity,
    itemClassID: entity.id,
    itemClassName: entity.name,
    totalUnavailableItems: entity.totalOutOfStocksItems,
    departmentID,
    departmentName,
    items: [], // Items will be fetched separately via MenuV3Items API
    itemWithoutPhotoCount: 0, // Will be calculated if needed
  }
}

/**
 * Maps array of HierarchyEntity to Department array
 */
export const mapHierarchyEntitiesToDepartments = (
  entities: HierarchyEntity[],
): Department[] => {
  return entities.map(entity => mapHierarchyEntityToDepartment(entity))
}

/**
 * Maps array of HierarchyEntity to SubDepartment array
 */
export const mapHierarchyEntitiesToSubDepartments = (
  entities: HierarchyEntity[],
  departmentID?: string,
  departmentName?: string,
): SubDepartment[] => {
  return entities.map(entity =>
    mapHierarchyEntityToSubDepartment(entity, departmentID, departmentName),
  )
}

/**
 * Maps MenuV3 ItemsV2 to Items
 * The API response already contains hierarchy context (itemClassID, parentItemClassID, categoryID)
 * This function just maps the field names to match the Item type
 */
export const mapItemsV2ToItems = (items: FoodmaxapipbItemV2[]): Item[] => {
  return (items.map(item => ({
    ...item,
    subDepartmentID: item.itemClassID,
    subDepartmentName: item.itemClassName,
    departmentID: item.parentItemClassID,
    departmentName: item.parentItemClassName,
  })) as unknown) as Item[]
}
